'use client';
import { useState } from 'react';
import { fireConversion } from '~/lib/gtag';
import type { Route } from './+types/quote';
import { Link } from 'react-router';
import { PRICING, calcPrice, formatPrice, type ServiceKey } from '~/data/site';
import { buildMeta } from '~/lib/seo';
import { isValidPhone } from '@shared/utils';

export const meta: Route.MetaFunction = () =>
  buildMeta({
    title: 'Get a Quote',
    description: 'Get your exact price in 90 seconds. No phone call required.',
    path: '/quote',
  });

type ServiceType = 'recurring' | 'deep' | 'moveout';
type FreqKey = 'weekly' | 'biweekly' | 'every4';
type DeepKey = 'maintenance' | 'fulldeep';

interface QuoteState {
  serviceType: ServiceType | null;
  sqft: number;
  frequency: FreqKey | null;
  deepType: DeepKey | null;
  deepUpgrade: boolean;
  address: string;
  city: string;
  zip: string;
  bedrooms: string;
  pets: boolean;
  accessNotes: string;
  name: string;
  phone: string;
  email: string;
  referral: string;
}

const FREQ_OPTIONS: { key: FreqKey; label: string; desc: string; rate: number; popular?: boolean }[] = [
  { key: 'weekly',   label: 'Weekly',        desc: 'Busy homes, young kids, pets.',          rate: PRICING.weeklyRate },
  { key: 'biweekly', label: 'Biweekly',      desc: 'The rhythm most clients settle into.',   rate: PRICING.biweeklyRate, popular: true },
  { key: 'every4',   label: 'Every 4 weeks', desc: 'Low-traffic, careful homes.',            rate: PRICING.monthly4Rate },
];

const DEEP_OPTIONS: { key: DeepKey; label: string; desc: string; rate: number }[] = [
  { key: 'maintenance', label: 'Maintenance clean', desc: 'For homes that just need a thorough reset.', rate: PRICING.maintenanceRate },
  { key: 'fulldeep',    label: 'Full deep clean',   desc: 'Inside vents, windows, grout, behind everything.', rate: PRICING.fullDeepRate },
];

function Progress({ step }: { step: number }) {
  return (
    <div className="quote-progress" aria-label={`Step ${step} of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <div
          key={n}
          className={`quote-dot${n < step ? ' done' : n === step ? ' active' : ''}`}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

export default function QuotePage() {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [q, setQ] = useState<QuoteState>({
    serviceType: null, sqft: 0,
    frequency: null, deepType: null, deepUpgrade: false,
    address: '', city: '', zip: '', bedrooms: '', pets: false, accessNotes: '',
    name: '', phone: '', email: '', referral: '',
  });

  function set<K extends keyof QuoteState>(key: K, val: QuoteState[K]) {
    setQ((prev) => ({ ...prev, [key]: val }));
  }

  // Price helpers
  const serviceKey: ServiceKey = q.serviceType === 'recurring'
    ? (q.frequency ?? 'biweekly')
    : q.serviceType === 'deep'
    ? (q.deepType ?? 'fulldeep')
    : 'moveout';
  const rate = q.serviceType === 'recurring'
    ? (q.frequency ? FREQ_OPTIONS.find((f) => f.key === q.frequency)!.rate : PRICING.biweeklyRate)
    : q.serviceType === 'deep'
    ? (q.deepType ? DEEP_OPTIONS.find((d) => d.key === q.deepType)!.rate : PRICING.fullDeepRate)
    : PRICING.moveOutRate;

  const price    = q.sqft > 0 ? calcPrice(q.sqft, serviceKey) : 0;
  // The deep upgrade is charged on band-1 sqft only — see shared/pricing.ts.
  const upgrade  = q.deepUpgrade && q.serviceType === 'recurring'
    ? Math.round(Math.min(q.sqft, PRICING.band1MaxSqft) * PRICING.deepUpgradeRate)
    : 0;
  const priceStr = price > 0 ? formatPrice(price) : '—';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);

    if (!isValidPhone(q.phone)) {
      setSubmitError('Please enter a valid 10-digit phone number so we can reach you.');
      setSubmitting(false);
      return;
    }

    try {
      const message = [
        `Service: ${q.serviceType}${q.frequency ? ` (${q.frequency})` : ''}${q.deepType ? ` (${q.deepType})` : ''}`,
        `Size: ${q.sqft.toLocaleString()} sqft`,
        `Address: ${q.address}, ${q.city} ${q.zip}`,
        q.bedrooms ? `Bedrooms: ${q.bedrooms}` : '',
        q.pets ? 'Pets: yes' : '',
        q.deepUpgrade ? 'First-visit deep upgrade: yes' : '',
        q.accessNotes ? `Access notes: ${q.accessNotes}` : '',
        q.referral ? `Referral: ${q.referral}` : '',
      ].filter(Boolean).join('\n');

      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: q.name.trim(),
          phone: q.phone.trim(),
          email: q.email.trim() || undefined,
          message,
          source: 'quote-flow',
        }),
      });
      if (!res.ok) throw new Error('submit failed');
      // /quote is the nav's and footer's primary CTA and had no conversion
      // event at all, so the site's main funnel was invisible to Google Ads.
      fireConversion();
      setSubmitted(true);
    } catch {
      setSubmitError('Something went wrong. Please try again, or email us directly.');
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <main className="section">
        <div className="container-narrow">
          <div className="quote-card">
            <div className="quote-success">
              <p className="display-md text-forest">We got it.</p>
              <p className="lede chapter-lede">
                We&rsquo;ll reach out to {q.name || 'you'} at {q.phone || 'the number you provided'} within 2 hours.
              </p>
              <div className="section-cta">
                <Link to="/" className="btn btn-ghost">← Back to home</Link>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="section bg-bone-warm">
      <div className="container-narrow">
        <p className="eyebrow page-eyebrow">The quote · 90 seconds</p>
        <h1 className="display-md page-h1-full">
          A few questions.{' '}
          <em className="italic-display">That&rsquo;s the whole thing.</em>
        </h1>

        <Progress step={step} />

        <div className="quote-card">

          {/* ── STEP 1: SERVICE TYPE ───────────────────── */}
          {step === 1 && (
            <div>
              <h2 className="display-sm">What type of service?</h2>
              <div className="quote-type-cards">
                {([
                  { key: 'recurring' as ServiceType, title: 'Recurring cleaning',    sub: 'Same crew, every visit' },
                  { key: 'deep'      as ServiceType, title: 'One-time deep clean',   sub: 'Full reset, top to bottom' },
                  { key: 'moveout'   as ServiceType, title: 'Move-in or move-out',   sub: 'Empty house, new chapter' },
                ]).map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    className={`quote-type-card${q.serviceType === opt.key ? ' selected' : ''}`}
                    onClick={() => { set('serviceType', opt.key); setStep(2); }}
                  >
                    <div className="quote-type-title">{opt.title}</div>
                    <div className="quote-type-sub">{opt.sub}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── STEP 2: SQUARE FOOTAGE ─────────────────── */}
          {step === 2 && (
            <div>
              <h2 className="display-sm">How big is your home?</h2>
              <div className="calc-row mt-8">
                <input
                  type="number"
                  className="calc-input"
                  value={q.sqft || ''}
                  min={100}
                  max={10000}
                  placeholder="2,400"
                  onChange={(e) => set('sqft', Math.max(0, parseInt(e.target.value) || 0))}
                  aria-label="Square footage"
                />
                <span className="lede lede-sm">sq ft</span>
              </div>

              {q.sqft > 0 && (
                <div className="calc-result mt-4">
                  <div className="calc-price">{priceStr}</div>
                  <p className="calc-formula">
                    {q.serviceType === 'recurring'
                      ? `${q.sqft.toLocaleString()} sqft — see frequency options next`
                      : `${q.sqft.toLocaleString()} sqft × $${rate.toFixed(2)}`}
                    {q.sqft * rate < PRICING.minimum ? ` — ${PRICING.minimum} minimum applies` : ''}
                  </p>
                </div>
              )}

              <details className="form-field">
                <summary className="btn-link summary-link">How do I measure?</summary>
                <p className="lede text-small mt-2">
                  Use your home&rsquo;s heated square footage (check your property tax records or
                  Zillow listing). Don&rsquo;t include garages. An estimate within 10% is fine.
                </p>
              </details>

              <p className="lede text-fine mt-4">
                All services start at ${PRICING.minimum}.
              </p>

              <div className="quote-nav">
                <button type="button" className="btn btn-ghost" onClick={() => setStep(1)}>← Back</button>
                <button
                  type="button"
                  className="btn btn-primary btn-lg"
                  disabled={!q.sqft || q.sqft < 100}
                  onClick={() => setStep(3)}
                >
                  Continue →
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 3: FREQUENCY / DEEP TYPE ─────────── */}
          {step === 3 && (
            <div>
              {q.serviceType === 'recurring' && (
                <>
                  <h2 className="display-sm">How often?</h2>
                  <div className="quote-freq-cards">
                    {FREQ_OPTIONS.map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        className={`quote-freq-card${q.frequency === opt.key ? ' selected' : ''}`}
                        onClick={() => set('frequency', opt.key)}
                      >
                        {opt.popular && <span className="quote-popular">Most popular</span>}
                        <div className="quote-freq-title">{opt.label}</div>
                        <div className="quote-freq-desc">{opt.desc}</div>
                        <div className="quote-freq-price">
                          {q.sqft > 0 ? formatPrice(calcPrice(q.sqft, opt.key)) : `$${opt.rate.toFixed(2)}/sqft`}
                        </div>
                      </button>
                    ))}
                  </div>
                  {q.frequency && (
                    <label className="calc-upgrade visible mt-6">
                      <input
                        type="checkbox"
                        checked={q.deepUpgrade}
                        onChange={(e) => set('deepUpgrade', e.target.checked)}
                      />
                      <span>
                        Add first-visit deep clean
                        {q.sqft > 0 && ` (+${formatPrice(Math.round(q.sqft * PRICING.deepUpgradeRate))})`}
                      </span>
                    </label>
                  )}
                </>
              )}

              {q.serviceType === 'deep' && (
                <>
                  <h2 className="display-sm">Which type of deep clean?</h2>
                  <div className="quote-subtype-cards mt-6">
                    {DEEP_OPTIONS.map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        className={`quote-freq-card${q.deepType === opt.key ? ' selected' : ''}`}
                        onClick={() => set('deepType', opt.key)}
                      >
                        <div className="quote-freq-title">{opt.label}</div>
                        <div className="quote-freq-desc">{opt.desc}</div>
                        <div className="quote-freq-price">
                          {q.sqft > 0 ? formatPrice(calcPrice(q.sqft, opt.key)) : `$${opt.rate.toFixed(2)}/sqft`}
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              )}

              {q.serviceType === 'moveout' && (
                <>
                  <h2 className="display-sm">Your move-in / move-out estimate</h2>
                  <div className="price-example reveal mt-6">
                    <p className="eyebrow">Unfurnished flat rate</p>
                    <div className="calc-price mt-2">{priceStr}</div>
                    <p className="calc-formula">
                      {q.sqft.toLocaleString()} sqft × ${PRICING.moveOutRate.toFixed(2)}
                      {q.sqft * PRICING.moveOutRate < PRICING.minimum
                        ? ` — $${PRICING.minimum} minimum applies`
                        : ''}
                    </p>
                  </div>
                  <p className="lede chapter-lede">
                    Walls excluded. Everything else: top to bottom, every room, including fixtures,
                    inside appliances, and cabinet interiors.
                  </p>
                </>
              )}

              <div className="quote-nav">
                <button type="button" className="btn btn-ghost" onClick={() => setStep(2)}>← Back</button>
                <button
                  type="button"
                  className="btn btn-primary btn-lg"
                  disabled={
                    (q.serviceType === 'recurring' && !q.frequency) ||
                    (q.serviceType === 'deep' && !q.deepType)
                  }
                  onClick={() => setStep(4)}
                >
                  Continue →
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 4: HOME DETAILS ───────────────────── */}
          {step === 4 && (
            <div>
              <h2 className="display-sm">Your home</h2>
              <div className="quote-form-grid mt-6">
                <label className="form-label">
                  <span className="form-label-text">Street address</span>
                  <input
                    className="form-input"
                    value={q.address}
                    onChange={(e) => set('address', e.target.value)}
                  />
                </label>
                <label className="form-label">
                  <span className="form-label-text">City</span>
                  <input
                    className="form-input"
                    value={q.city}
                    onChange={(e) => set('city', e.target.value)}
                  />
                </label>
              </div>
              <div className="quote-form-grid form-field">
                <label className="form-label">
                  <span className="form-label-text">ZIP code</span>
                  <input
                    className="form-input"
                    value={q.zip}
                    onChange={(e) => set('zip', e.target.value)}
                  />
                </label>
                <label className="form-label">
                  <span className="form-label-text">Bedrooms</span>
                  <select
                    className="form-input"
                    value={q.bedrooms}
                    onChange={(e) => set('bedrooms', e.target.value)}
                  >
                    <option value="">Select</option>
                    {['1', '2', '3', '4', '5', '6+'].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="form-label form-field">
                <input
                  type="checkbox"
                  checked={q.pets}
                  onChange={(e) => set('pets', e.target.checked)}
                />
                {' '}Pets in the home
              </label>
              <label className="form-label form-field">
                <span className="form-label-text">Any rooms we shouldn&rsquo;t enter?</span>
                <textarea
                  className="form-input"
                  rows={2}
                  placeholder="e.g. home office, guest room"
                  value={q.accessNotes}
                  onChange={(e) => set('accessNotes', e.target.value)}
                />
              </label>

              <div className="quote-nav">
                <button type="button" className="btn btn-ghost" onClick={() => setStep(3)}>← Back</button>
                <button
                  type="button"
                  className="btn btn-primary btn-lg"
                  disabled={!q.address || !q.city}
                  onClick={() => setStep(5)}
                >
                  Continue →
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 5: CONTACT + SUMMARY ──────────────── */}
          {step === 5 && (
            <form onSubmit={handleSubmit}>
              <h2 className="display-sm">Last step.</h2>
              <div className="quote-form-grid mt-6">
                <label className="form-label">
                  <span className="form-label-text">Your name</span>
                  <input
                    required
                    name="name"
                    autoComplete="name"
                    className="form-input"
                    value={q.name}
                    onChange={(e) => set('name', e.target.value)}
                  />
                </label>
                <label className="form-label">
                  <span className="form-label-text">Phone</span>
                  <input
                    required
                    type="tel"
                    name="phone"
                    autoComplete="tel"
                    inputMode="tel"
                    className="form-input"
                    value={q.phone}
                    onChange={(e) => set('phone', e.target.value)}
                  />
                </label>
              </div>
              <label className="form-label form-field">
                <span className="form-label-text">Email</span>
                <input
                  required
                  type="email"
                  name="email"
                  autoComplete="email"
                  inputMode="email"
                  className="form-input"
                  value={q.email}
                  onChange={(e) => set('email', e.target.value)}
                />
              </label>
              <label className="form-label form-field">
                <span className="form-label-text">How did you hear about us? (optional)</span>
                <select
                  className="form-input"
                  value={q.referral}
                  onChange={(e) => set('referral', e.target.value)}
                >
                  <option value="">Select one</option>
                  {['Google', 'Referral', 'Nextdoor', 'Instagram', 'Drove by', 'Other'].map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </label>

              {/* Quote summary */}
              <div className="quote-summary">
                <div className="quote-summary-row">
                  <span>Service</span>
                  <span>
                    {q.serviceType === 'recurring' ? `Recurring · ${q.frequency ?? ''}` :
                     q.serviceType === 'deep' ? `Deep clean · ${q.deepType ?? ''}` :
                     'Move-in / move-out'}
                  </span>
                </div>
                <div className="quote-summary-row">
                  <span>Size</span>
                  <span>{q.sqft.toLocaleString()} sqft</span>
                </div>
                <div className="quote-summary-row">
                  <span>Address</span>
                  <span>{q.address}, {q.city}</span>
                </div>
                <div className="quote-summary-row">
                  <span>Price per visit</span>
                  <span className="quote-summary-total">{priceStr}</span>
                </div>
                {q.deepUpgrade && upgrade > 0 && (
                  <div className="quote-summary-row">
                    <span>First visit (with deep clean)</span>
                    <span>{formatPrice(price + upgrade)}</span>
                  </div>
                )}
              </div>

              <p className="lede chapter-lede text-fine mt-4">
                This is your number. It doesn&rsquo;t change.
              </p>

              {submitError && (
                <p className="form-error-msg mt-3">{submitError}</p>
              )}

              <div className="quote-nav">
                <button type="button" className="btn btn-ghost" onClick={() => setStep(4)}>← Back</button>
                <button type="submit" className="btn btn-brass btn-lg" disabled={submitting}>
                  {submitting ? 'Sending…' : 'Get this quote →'}
                </button>
              </div>
            </form>
          )}

        </div>

        <p className="marginalia chapter-marginalia text-center">
          No deposit. No contract. Cancel any visit, 24 hours&rsquo; notice.
        </p>
      </div>
    </main>
  );
}
